import {instance} from '@/api/axiosConfig'
import {
	BetslipInfoRequest,
	BetslipInfoResponse,
	DeepLinkRequest,
	DeepLinkResponse,
	GenerateShareRequest,
	MarketDataRequest,
	MarketDataResponse,
	PriceOutcome,
} from '@/types/client'
import {AxiosRequestConfig, AxiosResponse} from 'axios'
import {requestConfig} from '@/api/authorisation'
import {ComposeResponse} from '@/types/conductor'

const REQUEST_CLIENT_MARKET_DATA = `/marketData`
const REQUEST_CLIENT_DEEP_LINK = `/deepLink`
const REQUEST_CLIENT_BETSLIP_INFO = `/betSlipInfo`
const REQUEST_CLIENT_ODDS = `/odds`
const REQUEST_CLIENT_SHARE = `/share/generate-share`
const REQUEST_CLIENT_SHARE_WITH_IMAGE = `/share/generate-share-with-image`
const REQUEST_CLIENT_FETCH_SHARE = `/share/fetch-share`

let _requestId = 100

function allocateRequestId(): number {
	console.log(`Last client requestId: ${_requestId}`)
	const newRequestId = ++_requestId
	console.log(`New client requestId: ${_requestId}`)
	return newRequestId
}

export interface ShareWithImageResponse {
	url: string
	imageBase64: string
}

export interface FetchedShare {
	imageUrl: string
	query: string
	shareId: string
	deepLinkURL: string
	path?: string
	state?: any
	brand?: string
	shareUrl?: string
	betSlipImageData?: any
}

export default {
	async getMarketData(
			context: string,
			marketDataRequest: MarketDataRequest,
	): Promise<MarketDataResponse> {
		const config: AxiosRequestConfig = requestConfig()
		marketDataRequest.requestId = allocateRequestId()
		const data: any = {
			marketDataRequest,
		}
		const response: AxiosResponse = await instance.post(
				REQUEST_CLIENT_MARKET_DATA + '/' + context,
				data,
				config,
		)
		return response?.data
	},

	async getDeepLink(
			context: string,
			deepLinkRequest: DeepLinkRequest,
	): Promise<DeepLinkResponse> {
		const config: AxiosRequestConfig = requestConfig()
		deepLinkRequest.requestId = allocateRequestId()
		const data: any = {
			deepLinkRequest,
		}
		const response: AxiosResponse = await instance.post(
				REQUEST_CLIENT_DEEP_LINK + '/' + context,
				data,
				config,
		)
		return response?.data
	},

	async getBetslipInfo(
			context: string,
			betslipInfoRequest: BetslipInfoRequest,
	): Promise<BetslipInfoResponse> {
		betslipInfoRequest.requestId = allocateRequestId()
		const data: any = {
			betSlipInfoRequest: betslipInfoRequest,
		}

		const url: string = `${REQUEST_CLIENT_BETSLIP_INFO}/${context}`
		const config: AxiosRequestConfig = requestConfig()
		const response: AxiosResponse = await instance.post(url, data, config)

		return response?.data
	},

	async getOdds(
			context: string,
			composeResponse: ComposeResponse,
	): Promise<PriceOutcome[]> {

		const url: string = `${REQUEST_CLIENT_ODDS}/${context}`;
		const config: AxiosRequestConfig = requestConfig();
		const response: AxiosResponse<PriceOutcome[]> = await instance.post(url, composeResponse, config);

		return response.data;
	},

	async generateShare(
			request: GenerateShareRequest,
	): Promise<string> {
		console.log('**** API CALL: generateShare (Routing via image endpoint for iOS compatibility) ****')
		try {
			// We use the image-based endpoint because it returns a proper JSON object.
			// The simple generate-share returns a raw string which crashes CapacitorHttp on iOS
			// if the server still sends Content-Type: application/json.
			const res = await this.generateShareWithImage(request)
			console.log('**** API SUCCESS: generateShare (via image endpoint) ****', res.url)
			return res.url
		} catch (err) {
			console.error('**** API ERROR: generateShare ****', err)
			throw err
		}
	},

	async generateShareWithImage(
			request: GenerateShareRequest,
	): Promise<ShareWithImageResponse> {
		const config: AxiosRequestConfig = requestConfig()

		const localConfig: AxiosRequestConfig = {
			...config,
			headers: {
				...config.headers,
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			}
		}

		console.log('**** API CALL: generateShareWithImage', JSON.stringify(request))
		try {
			const response: AxiosResponse<ShareWithImageResponse> = await instance.post(
					REQUEST_CLIENT_SHARE_WITH_IMAGE,
					request,
					localConfig,
			)
			console.log('**** API SUCCESS: generateShareWithImage', response.data.url)
			return response.data
		} catch (err) {
			console.error('**** API ERROR: generateShareWithImage', err)
			throw err
		}
	},

	async fetchShareById(
			shareId: string,
	): Promise<FetchedShare> {
		const config: AxiosRequestConfig = requestConfig()

		const localConfig: AxiosRequestConfig = {
			...config,
			headers: {
				...config.headers,
				'Accept': 'application/json',
			}
		}

		const response: AxiosResponse<FetchedShare> = await instance.get(
				REQUEST_CLIENT_FETCH_SHARE + `/${shareId}`, localConfig
		)

		console.log('fetchShareById response:', response.data)
		const fetchedShare = response.data

		const urlToCheck = fetchedShare.path || fetchedShare.deepLinkURL
		if (urlToCheck) {
			try {
				const url = new URL(urlToCheck, window.location.origin)
				let s = url.searchParams.get('s')
				if (!s && url.hash.includes('?')) {
					const hashQuery = url.hash.split('?')[1]
					const hashParams = new URLSearchParams(hashQuery)
					s = hashParams.get('s')
				}
				if (s && !fetchedShare.state) {
					fetchedShare.state = s
				}
			} catch (e) {
				console.error('Failed to parse URL for state', e)
			}
		}

		if (fetchedShare.state) {
			let stateObj: any = null;
			if (typeof fetchedShare.state === 'object') {
				stateObj = fetchedShare.state;
			} else if (typeof fetchedShare.state === 'string') {
				try {
					stateObj = JSON.parse(fetchedShare.state);
				} catch (e) {}
			}
			
			if (stateObj && stateObj.betSlipImageData && !fetchedShare.betSlipImageData) {
				fetchedShare.betSlipImageData = stateObj.betSlipImageData;
			}
		}

		return fetchedShare
	}
}
